import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const LINKS = [
  { label: "Production", id: "#production" },
  { label: "Stories", id: "#stories" },
  { label: "Writing Room", id: "#writing-room" },
  { label: "Collaborate", id: "#collaborate" },
  { label: "Contact", id: "#contact" },
];

export default function GoshtaHeader() {
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="goshta-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 md:h-[72px] md:px-10">
        <button
          data-testid="goshta-nav-logo"
          onClick={() => go("#top")}
          className="group flex items-center gap-3 text-left"
        >
          <img
            src="/assets/goshta-mark.png"
            alt="Goshta logo"
            className="h-9 w-9 rounded-full border border-[#B79045]/50 object-cover transition-transform duration-700 group-hover:rotate-[20deg] md:h-10 md:w-10"
          />
          <span className="flex flex-col leading-none">
            <span className="font-mono text-lg font-bold tracking-[0.18em] text-white transition-colors duration-300 group-hover:text-[#D4AF37] md:text-xl">
              GOSHTA
            </span>
            <span className="hidden font-mono text-[8px] tracking-[0.28em] text-neutral-500 sm:block">
              THE WRITER ROOM PRODUCTIONS
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`goshta-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(l.id)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-neutral-400 transition-colors duration-300 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#B79045] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <Link
            data-testid="goshta-nav-veritas-link"
            to="/veritas"
            className="border border-white/15 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500 transition-colors duration-300 hover:border-[#B79045]/60 hover:text-[#D4AF37]"
          >
            A Veritas Advisory Co.
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="goshta-nav-cta"
            onClick={() => go("#contact")}
            className="hidden items-center gap-2 border border-[#B79045]/60 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#B79045] hover:text-black sm:inline-flex"
          >
            Let&apos;s Talk <ArrowUpRight size={14} />
          </button>
          <button
            data-testid="goshta-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-neutral-200 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-white/10 bg-black/90 lg:hidden"
          >
            <div className="flex flex-col gap-5 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`goshta-mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-2xl text-neutral-200 transition-colors duration-300 hover:text-[#D4AF37]"
                >
                  {l.label}
                </button>
              ))}
              <Link
                data-testid="goshta-mobile-veritas-link"
                to="/veritas"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500"
              >
                A Veritas Advisory Company →
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
